<template>
  <div
    class="sidebar"
    :class="{ 'sidebar--minimized': uiStore.sidebarMinimized }"
  >
    <SidebarHeader />
    <div class="sidebar__menus">
      <DarwinButton
        v-if="!isLoggedIn"
        class="sidebar__redirect-btn"
        size="md"
        flair="rounded"
        kind="secondary"
        @click="goToSignUp"
      >
        Sign up to import
      </DarwinButton>

      <ToolTip
        v-for="{ show, label, dataTest, ...props } in topMenu"
        :key="label"
        placement="right"
        :delay="[500, 0]"
        :content="getTooltip(label)"
      >
        <SidebarItem
          v-if="show ?? true"
          v-bind="props"
          :minimized="uiStore.sidebarMinimized"
          :data-test="dataTest"
        >
          {{ label }}
        </SidebarItem>
      </ToolTip>
    </div>
    <div class="sidebar__bottom">
      <div class="sidebar__menus">
        <template v-for="{ show, label, events, dataTest, ...props } in bottomMenu">
          <ToolTip
            v-if="show ?? true"
            :key="label"
            :delay="[500, 0]"
            placement="right"
            :content="getTooltip(label)"
          >
            <SidebarItem
              v-bind="props"
              :data-test="dataTest"
              :minimized="uiStore.sidebarMinimized"
              class="sidebar__menu--pos-bottom"
              @click="events?.click"
            >
              {{ label }}
            </SidebarItem>
          </ToolTip>
        </template>
        <SidebarAddWorkspaceDialog
          v-if="teamStore.currentTeam"
          :id="teamStore.currentTeam.id"
          v-model:open="isAddWorkspaceDialogShown"
          :slug="teamStore.currentTeam.slug"
        />
      </div>
      <div class="sidebar__bottom__footer">
        <a
          v-if="!uiStore.sidebarMinimized"
          href="https://v7labs.com/"
          rel="noreferrer noopener"
          target="_blank"
        >
          <LogoIcon />
        </a>
        <IconButton
          size="xs"
          flair="rounded"
          kind="transparent"
          @click="toggleSidebar"
        >
          <Icon
            class="toggle-icon"
            :name="uiStore.sidebarMinimized ? 'mono-collapse-right' : 'mono-collapse-left'"
          />
        </IconButton>
      </div>
    </div>
    <DarwinDialog
      :open="isUpgradeDialogShown"
      fullscreen
      max-height="900"
      @keydown:esc="isUpgradeDialogShown = false"
    >
      <UpgradeDialog
        v-if="isUpgradeDialogShown"
        :url="talkToSalesUrls.WORKSPACE_ADDON_PURCHASE"
        @close="isUpgradeDialogShown = false"
      >
        <template #title>Upgrade your plan to add more workspaces</template>
        <template #subtitle
          >Your workspace is on a plan that doesn't allow additional workspaces. Upgrade to remove
          limits and access additional features.</template
        >
        <UpgradeDialogWorkspaces />
      </UpgradeDialog>
    </DarwinDialog>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import LogoIcon from '@/components/Common/LogoIcon.vue'
import SidebarItem from '@/layouts/Main/Sidebar/SidebarItem.vue'
import SidebarHeader from '@/components/Layout/Sidebar/SidebarHeader.vue'
import { useAuth } from '@/modules/Auth/useAuth'
import { useTeamStore } from '@/pinia/useTeamStore'
import { useFeatureFlagsStore } from '@/pinia/useFeatureFlagsStore'

import SidebarAddWorkspaceDialog from '@/layouts/Main/Sidebar/SidebarAddWorkspaceDialog.vue'

import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import DarwinDialog from '@/uiKit/Dialog/DarwinDialog.vue'
import Icon from '@/uiKit/Icon/Icon.vue'
import IconButton from '@/uiKit/Button/IconButton.vue'

import { useResourceWorkspace } from '@/modules/Billing/useResourceWorkspace'
import { useUpgradeLocation } from '@/modules/Billing/useUpgradeLocation'
import UpgradeDialog from '@/modules/Billing/UpgradeDialog.vue'
import UpgradeDialogWorkspaces from '@/modules/Billing/UpgradeDialogWorkspaces.vue'
import { useDerivedResourceIntercom } from '@/modules/Billing/useDerivedResourceIntercom'
import { useUserStore } from '@/modules/Auth/useUserStore'
import ToolTip from '@/uiKit/ToolTip/ToolTip.vue'
import { useMembershipStore } from '@/modules/Teams/useMembershipStore'
import { useUIStore } from '@/pinia/useUIStore'
import { useIntercomLoader } from '@/composables/useIntercomLoader'
import { getSidebarCollapsedWidth, getSidebarExpandedWidth } from '@/uiKitLegacy/theme'
import { onMounted } from 'vue'

const teamStore = useTeamStore()
const membershipStore = useMembershipStore()
const uiStore = useUIStore()

const userStore = useUserStore()
const isSuperUser = computed(() => !!userStore.currentUser?.superuser)
const isLoggedIn = computed(() => !!userStore.currentUser)

const membershipId = computed(() =>
  userStore.currentUserId ? membershipStore.memberIdByUserId[userStore.currentUserId] : undefined,
)

const { isAuthorized } = useAuth()
const isNotAnnotator = computed(() => !!isAuthorized('view_full_datasets'))

const showDatasetsMenuItem = computed(() => {
  // user is team member or higher -> show
  if (isNotAnnotator.value) {
    return true
  }

  // user is not logged in -> show, but as disabled
  if (!isLoggedIn.value) {
    return true
  }

  // user is annotator, nohing to show in datasets
  if (!isNotAnnotator.value) {
    return false
  }

  // user is not team member, viewing open datasets -> show
  if (!membershipId.value) {
    return true
  }

  return false
})

const flagsStore = useFeatureFlagsStore()

const showReportsMenuItem = computed(
  () => isLoggedIn.value && isAuthorized('view_annotation_report'),
)

const showRulesMenuItem = computed(
  () =>
    isLoggedIn.value && isNotAnnotator.value && flagsStore.featureFlags.LLM_ASSISTED_REVIEW_STAGE,
)

const { intercomSupport } = useDerivedResourceIntercom()

const intercomLoader = useIntercomLoader()

const repositionIntercom = (): void => {
  const sidebarWidth = uiStore.sidebarMinimized
    ? getSidebarCollapsedWidth()
    : getSidebarExpandedWidth()
  intercomLoader.positionIntercom(sidebarWidth + 10)
}

const toggleSidebar = (): void => {
  uiStore.setSidebarMinimized(!uiStore.sidebarMinimized)
  repositionIntercom()
}

const goToSignUp = (): void => {
  window.location.href = 'https://www.v7labs.com/sign-up'
}

const topMenu = computed(() => [
  {
    label: 'Datasets',
    icon: 'duotone-status-dataset' as const,
    to: { name: 'DatasetsIndex' },
    show: showDatasetsMenuItem.value,
    disabled: !isLoggedIn.value,
    dataTest: 'Datasets',
  },
  {
    label: isNotAnnotator.value ? 'Workflows' : 'Work Queue',
    icon: 'duotone-workflow' as const,
    to: { name: 'Workflows' },
    show: true,
    dataTest: 'Workflows',
  },
  {
    label: 'Classes',
    icon: 'duotone-tag' as const,
    to: { name: 'Classes' },
    show: isNotAnnotator.value,
    disabled: !isLoggedIn.value,
    dataTest: 'Classes',
  },
  {
    label: 'Models',
    icon: 'duotone-status-model' as const,
    to: { name: 'ModelsIndex' },
    show: isLoggedIn.value && isNotAnnotator.value,
    dataTest: 'Models',
  },
  {
    label: 'Reports',
    icon: 'mono-report' as const,
    to: { name: 'Reports' },
    show: showReportsMenuItem.value,
    dataTest: 'Reports',
  },
  {
    label: 'Rules',
    icon: 'mono-ai-assisted-review-stage' as const,
    to: { name: 'RulesLibrary' },
    show: showRulesMenuItem.value,
    dataTest: 'Rules',
  },
])

const isAddWorkspaceDialogShown = ref(false)
const isUpgradeDialogShown = ref(false)

const { isWorkspaceAtLimit } = useResourceWorkspace()

const talkToSalesUrls = useUpgradeLocation()

const bottomMenu = computed(() => [
  ...(intercomSupport.value === 'available'
    ? [
        {
          label: 'Support',
          icon: 'duotone-comment' as const,
          id: 'feedback-trigger',
          show: isLoggedIn.value,
          target: '_blank' as const,
          dataTest: 'Support',
        },
      ]
    : []),
  ...(intercomSupport.value === 'unavailable'
    ? [
        {
          label: 'Community Forum',
          icon: 'duotone-comment' as const,
          show: isLoggedIn.value,
          href: 'https://v7-community.circle.so/',
          target: '_blank' as const,
          dataTest: 'Support',
        },
      ]
    : []),
  {
    label: 'Changelog',
    icon: 'duotone-changelog' as const,
    href: 'https://www.v7labs.com/darwin/changelog',
    target: '_blank' as const,
    dataTest: 'Changelog',
  },
  {
    label: 'Academy',
    icon: 'duotone-video' as const,
    href: 'https://www.v7labs.com/academy',
    target: '_blank' as const,
    dataTest: 'Academy',
  },
  {
    label: 'Documentation',
    icon: 'duotone-document' as const,
    href: 'https://docs.v7labs.com/',
    target: '_blank' as const,
    dataTest: 'Documentation',
  },
  ...(userStore.isAnnotator || userStore.isWorkforceManager
    ? []
    : [
        {
          label: 'Add workspace',
          icon: 'duotone-add-person' as const,
          disabled: isAddWorkspaceDialogShown.value || isUpgradeDialogShown.value,
          events: {
            click: (): void => {
              isWorkspaceAtLimit.value
                ? (isUpgradeDialogShown.value = true)
                : (isAddWorkspaceDialogShown.value = true)
            },
          },
          dataTest: 'Add workspace',
        },
      ]),
  {
    label: 'Settings',
    icon: 'duotone-settings' as const, // temp
    to: { query: { settings: 'profile' } },
    exact: true,
    show: isLoggedIn.value,
    dataTest: 'Settings',
  },
  {
    label: 'Admin',
    icon: 'duotone-admin' as const, // temp
    href: '/super_admin',
    show: isSuperUser.value,
    dataTest: 'Admin',
  },
])

const getTooltip = (content: string): string | null => (uiStore.sidebarMinimized ? content : null)

onMounted((): void => {
  repositionIntercom()
})
</script>

<!-- eslint-disable vue-scoped-css/enforce-style-type -->
<style src="./tooltip.scss" lang="scss"></style>

<style lang="scss" scoped>
@import '@/uiKit/assets/transitions';

.sidebar {
  width: $sidebarExpandedWidth;
  height: 100%;
  background: var(--color-surface-tertiary);
  position: relative;

  transition: width $transitionDefault;

  @include col;
}

.sidebar__menus {
  width: 100%;
  flex: 1 1 auto;
  overflow: auto;
  overflow-x: hidden;
  gap: 2px;
  padding: 6px 8px 8px 8px;
  @include col;
}

.sidebar__menu--pos-bottom {
  height: var(--size-9);
}

.sidebar__bottom__footer {
  @include row--distributed;
  height: 30px;
  margin: 0 20px 10px 20px;
  align-items: center;
}

.sidebar--minimized {
  width: $sidebarCollapsedWidth;

  .sidebar__bottom__footer {
    margin: 0 0 10px 0;
    justify-content: center;
  }
}

.sidebar__redirect-btn {
  display: flex;
  margin: 4px auto;
}

.toggle-icon {
  font-size: 20px;
  color: var(--color-icon-subtle);
}
</style>
